import { useEffect, useState } from 'react';

import { ButtonWithLinkStyle, SubmitButton } from '@capsule/consumer-design-system';

import { addBreadcrumb, captureException } from '@sentry/react';
import { Form, Formik } from 'formik';
import qs from 'query-string';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';
import * as Yup from 'yup';

import GenericError from 'src/app/components/Error/Generic';
import { SegmentFormIncomplete } from 'src/app/components/Error/SegmentFormIncomplete';
import TextInput from 'src/app/components/Form/TextInput';
import { MFAResendDialog } from 'src/app/components/Pages/MFA/MFAResendDialog';
import { LoginMfaOption } from 'src/app/context/AuthContext';
import { useAccount } from 'src/app/hooks/data/account';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { login as loginPath, newOnboardingStep } from 'src/app/router/paths';
import { getLoginRedirectPath } from 'src/app/router/utils';
import { authRedirect, sessionSetCurrentUserId } from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';
import { HAS_SEEN_MFA_INTERSTIAL_IN_PAST } from 'src/app/storage/localStorageKeys';
import { isHydra } from 'src/app/utils/browser';

import Spinner from '../../Elements/Spinner';
import { EscriptOnboardingStepName } from '../EscriptOnboarding/types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: ${({ theme }) => theme.space.s8} auto;
  width: 500px;
  max-width: calc(100vw - (48px * 2));

  ${({ theme }) => theme.mediaQueries.smallDown} {
    margin: ${({ theme }) => theme.space.s6} auto;

    & > form > * {
      margin-bottom: ${({ theme }) => theme.space.s5} !important;
    }
  }

  &&& > h1 {
    padding: 0;
    font-size: 2.8rem !important;
  }

  & > form > * {
    margin-bottom: ${({ theme }) => theme.space.s7} !important;
  }

  & > form > p {
    margin-bottom: ${({ theme }) => theme.space.s8} !important;
  }

  & > form > *:last-child {
    margin: 0 !important;
  }
`;

/**
 * We have really liberal validation here to rely on AWS's validations.
 * If AWS decided to change the code format and we had client-side validation, users would be unable to login.
 */
export const MFAForm = () => {
  const dispatch = useDispatch();
  const [isResendDialogOpen, setResendDialogOpen] = useState(false);
  const [selectedMfaDeliveryMedium, setSelectedMfaDeliveryMedium] = useState(LoginMfaOption.SMS);

  const segmentTrackLoginSuccessful = useSegmentEvent('loginSuccessful', { shouldTrackOnMount: false });
  const segmentTrackLoginFailed = useSegmentEvent('loginFailed', { shouldTrackOnMount: false });
  const segmentTrackFormSubmit = useSegmentEvent('submitVerificationCode', { shouldTrackOnMount: false });
  const segmentTrackFormSubmitFailure = useSegmentEvent('submitVerificationCodeFailed', { shouldTrackOnMount: false });
  const segmentTrackFormSubmitSuccess = useSegmentEvent('submitVerificationCodeSuccessful', {
    shouldTrackOnMount: false,
  });

  const { replace } = useHistory();
  const location = useLocation();
  const { flow } = qs.parse(location.search);

  const { loginContext, confirmMfaCode, loggedInUser, mfaContext } = useAuth();
  const { data: user, isSuccess: isLoadUserSuccess } = useUser(
    loggedInUser ? loggedInUser.uuid : '',
    Boolean(loggedInUser)
  );

  const { data: account, isFetching: isAccountFetching } = useAccount(user ? parseInt(user.accountId) : -1, {
    enabled: Boolean(user),
  });

  const validationSchema = Yup.object({
    mfaCode: Yup.string().default('').required('Please enter the verification code'),
  });
  const initialValues = {
    mfaCode: '',
  };

  useEffect(() => {
    window.localStorage.setItem(HAS_SEEN_MFA_INTERSTIAL_IN_PAST, 'true');
  }, []);

  useEffect(() => {
    const loadUserAndRedirect = async () => {
      if (user && loggedInUser) {
        await dispatch(sessionSetCurrentUserId(user.userId, user.accountId));

        if (flow) {
          return replace(getLoginRedirectPath(location.search));
        }

        // Check for onboarding flow. Signup source should be escript, and isLoginSet should be false
        if (isHydra() || (isLoadUserSuccess && account?.signupSource === 'escript' && !user.isLoginSet)) {
          return replace(newOnboardingStep.toPath({ stepName: EscriptOnboardingStepName.redirect }));
        }

        await dispatch(authRedirect());
      }
    };

    if (user && loggedInUser) {
      loadUserAndRedirect();
    }
  }, [user, account, loggedInUser, dispatch, replace, flow, location.search, isLoadUserSuccess]);

  useEffect(() => {
    if (!mfaContext) {
      replace(loginPath.route);
    }
  }, [mfaContext, replace]);

  if (isAccountFetching) {
    return <Spinner centered />;
  }

  return (
    <Container>
      <h1 className="h4 font-h-4">Enter your code</h1>

      <Formik
        onSubmit={async ({ mfaCode }, { setErrors, setSubmitting }) => {
          setSubmitting(true);

          try {
            await confirmMfaCode(mfaCode);

            segmentTrackFormSubmit();
            segmentTrackFormSubmitSuccess();
            segmentTrackLoginSuccessful();
          } catch (error: any) {
            const errorSummary: string = error?.kind ?? 'Unknown';

            segmentTrackFormSubmit();
            segmentTrackFormSubmitFailure({ error_code: 'Amplify', error_summary: errorSummary });
            segmentTrackLoginFailed({ error_code: 'Amplify', error_summary: errorSummary });

            const setFormError = (errorMessage: string) => {
              // @ts-expect-error - GenericError makes it so that _generic is an available field.
              setErrors({ _generic: errorMessage });
            };

            // Checkout link custom auth MFA flow error.
            if (error.name === 'CustomMfaInvalidCodeError') {
              const expiresAt = mfaContext?.codeExpiresAt;
              if (expiresAt && Math.floor(Date.now() / 1000) > parseInt(expiresAt)) {
                setFormError('The code has expired. Please request another code.');
              } else {
                setFormError('Incorrect code, please try again.');
              }
            } else if (error.name === 'CodeMismatchException') {
              setFormError('Incorrect code, please try again.');
            } else if (error.name === 'ExpiredCodeException' || error.name === 'NotAuthorizedException') {
              setFormError('The code has expired. Please request another code.');
            } else {
              setFormError(
                'Something isn’t quite right on our end... Please try again or contact the pharmacy for help.'
              );
              addBreadcrumb({ message: 'After submitting alleged SMS code on MFA form.' });
              captureException(error);
            }
          }

          setSubmitting(false);
        }}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, resetForm, setErrors }) => {
          return (
            <Form>
              <GenericError />
              <SegmentFormIncomplete />
              <p>
                {selectedMfaDeliveryMedium === LoginMfaOption.EMAIL
                  ? `Sent via email to ${loginContext.email}`
                  : `Sent via SMS to ${mfaContext?.codeDeliveryDestination}`}
              </p>

              <TextInput
                name="mfaCode"
                type="text"
                maxLength={6}
                label="Enter verification code"
                autoComplete="one-time-code"
                inputMode="numeric"
                data-testId="mfaCodeInput"
              />

              <ButtonWithLinkStyle onClick={() => setResendDialogOpen(true)}>
                {!!loginContext.checkoutCode ? 'Resend code' : 'Try another way'}
              </ButtonWithLinkStyle>
              <MFAResendDialog
                isResendDialogOpen={isResendDialogOpen}
                resetMFAForm={resetForm}
                setSelectedMfaDeliveryMedium={setSelectedMfaDeliveryMedium}
                setResendDialogOpen={setResendDialogOpen}
                // @ts-expect-error - GenericError makes it so that _generic is an available field.
                setMFAFormErrors={setErrors}
                isEmailOptionDisabled={!!loginContext.checkoutCode}
              />

              <SubmitButton variant="contained-secondary-lg" isSubmitting={isSubmitting}>
                Continue
              </SubmitButton>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};
